/* Webastoasennus theme – core behaviour. No dependencies. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const theme = window.theme || {};

  /* ------------------------------------------------------------ Focus trap for dialogs */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let lastFocus = null;
  function trapFocus(container) {
    lastFocus = document.activeElement;
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    container._untrap = () => container.removeEventListener('keydown', onKey);
  }
  function releaseFocus(container) {
    container._untrap && container._untrap();
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }
  const lockScroll = (on) => { document.documentElement.style.overflow = on ? 'hidden' : ''; };

  /* ------------------------------------------------------------ Header */
  const header = $('[data-header]');
  if (header) {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (header.hasAttribute('data-hide-on-scroll') && !header.classList.contains('is-open')) {
        header.classList.toggle('is-hidden', y > lastY && y > 320);
      }
      lastY = y;
    };
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Mega menu: click/Enter opens, hover opens with intent delay on fine pointers.
    const scrim = $('[data-nav-scrim]');
    const triggers = $$('[data-mega-trigger]', header);
    let hoverTimer;
    const closeAll = (except) => {
      triggers.forEach((t) => {
        if (t === except) return;
        t.setAttribute('aria-expanded', 'false');
        document.getElementById(t.getAttribute('aria-controls'))?.classList.remove('is-open');
      });
      if (!except) { header.classList.remove('is-open'); scrim?.classList.remove('is-on'); }
    };
    const open = (t) => {
      closeAll(t);
      t.setAttribute('aria-expanded', 'true');
      document.getElementById(t.getAttribute('aria-controls'))?.classList.add('is-open');
      header.classList.add('is-open');
      scrim?.classList.add('is-on');
    };
    triggers.forEach((t) => {
      t.addEventListener('click', () => (t.getAttribute('aria-expanded') === 'true' ? closeAll() : open(t)));
      const item = t.closest('.nav__item');
      if (matchMedia('(hover: hover)').matches) {
        item.addEventListener('mouseenter', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => open(t), 90); });
        item.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => closeAll(), 180); });
      }
    });
    scrim?.addEventListener('click', () => closeAll());
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openTrigger = triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
      if (openTrigger) { closeAll(); openTrigger.focus(); }
    });
    header.addEventListener('focusout', (e) => { if (!header.contains(e.relatedTarget)) closeAll(); });
  }

  /* ------------------------------------------------------------ Mobile menu */
  const drawer = $('[data-menu-drawer]');
  const burger = $('[data-menu-open]');
  if (drawer && burger) {
    const setOpen = (on) => {
      drawer.classList.toggle('is-open', on);
      burger.setAttribute('aria-expanded', String(on));
      lockScroll(on);
      if (on) { trapFocus(drawer); setTimeout(() => $('[data-menu-close]', drawer).focus(), 50); }
      else releaseFocus(drawer);
    };
    burger.addEventListener('click', () => setOpen(true));
    $('[data-menu-close]', drawer).addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) setOpen(false); });
    matchMedia('(min-width: 1100px)').addEventListener('change', (e) => e.matches && drawer.classList.contains('is-open') && setOpen(false));
  }

  /* ------------------------------------------------------------ Reveal on scroll */
  const reveal = () => {
    const els = $$('[data-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduceMotion.matches) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  };
  reveal();
  document.addEventListener('shopify:section:load', reveal);
  window.themeReveal = reveal;

  /* ------------------------------------------------------------ Parallax */
  const parallax = () => {
    if (reduceMotion.matches) return;
    const els = $$('[data-parallax]:not([data-px])');
    if (!els.length) return;
    els.forEach((el) => { el.dataset.px = ''; });
    let raf = null;
    const update = () => {
      $$('[data-parallax]').forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const p = (r.top + r.height / 2 - innerHeight / 2) / innerHeight;
        el.style.transform = `translate3d(0, ${(p * -7).toFixed(2)}%, 0)`;
      });
      raf = null;
    };
    if (!window._pxBound) {
      window._pxBound = true;
      addEventListener('scroll', () => { raf = raf || requestAnimationFrame(update); }, { passive: true });
    }
    update();
  };
  parallax();
  document.addEventListener('shopify:section:load', parallax);

  /* ------------------------------------------------------------ Hero video */
  $$('[data-hero]').forEach((hero) => {
    const video = $('video', hero);
    const toggle = $('[data-hero-toggle]', hero);
    const gauge = $('[data-hero-gauge]', hero);
    const ready = () => hero.classList.add('is-ready');
    // The entrance plays over the poster; the film fades in underneath when it can play.
    requestAnimationFrame(() => setTimeout(ready, 60));
    if (!video) return;

    const desktop = matchMedia('(min-width: 900px)');
    const pickSrc = () => (desktop.matches ? video.dataset.srcDesktop : video.dataset.srcMobile) || video.dataset.srcMobile || video.dataset.srcDesktop;
    const setToggle = (playing) => {
      if (!toggle) return;
      toggle.hidden = false;
      toggle.setAttribute('aria-pressed', String(!playing));
      $('[data-when="playing"]', toggle).hidden = !playing;
      $('[data-when="paused"]', toggle).hidden = playing;
    };
    const load = () => {
      const src = pickSrc();
      if (!src || video.getAttribute('src') === src) return;
      video.src = src;
      video.load();
    };
    const play = () => video.play().then(() => setToggle(true)).catch(() => setToggle(false));

    video.addEventListener('playing', () => video.classList.add('is-playing'));
    // Autoplay can be refused while the page is still hidden or buffering: retry when it can play
    // or becomes visible, unless the visitor paused it or prefers reduced motion.
    const retry = () => { if (video.src && video.paused && !hero.dataset.userPaused && !reduceMotion.matches && document.visibilityState === 'visible') play(); };
    video.addEventListener('canplay', retry);
    document.addEventListener('visibilitychange', retry);
    if (gauge) {
      video.addEventListener('timeupdate', () => {
        if (video.duration) gauge.style.setProperty('--fill', (video.currentTime / video.duration).toFixed(3));
      });
    }
    toggle?.addEventListener('click', () => {
      if (video.paused) { load(); play(); hero.dataset.userPaused = ''; }
      else { video.pause(); setToggle(false); hero.dataset.userPaused = '1'; }
    });

    const start = () => {
      if (reduceMotion.matches) { setToggle(false); return; } // poster only; user can press play
      load();
      play();
    };
    // Wait for the page to settle so the film never competes with critical resources.
    if (document.readyState === 'complete') setTimeout(start, 200);
    else addEventListener('load', () => setTimeout(start, 200), { once: true });

    desktop.addEventListener('change', () => { if (!video.paused || video.src) { load(); if (!hero.dataset.userPaused) play(); } });
    // Pause off-screen to save battery.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        if (!video.src || hero.dataset.userPaused || reduceMotion.matches) return;
        en.isIntersecting ? play() : video.pause();
      }, { threshold: 0.15 }).observe(hero);
    }
  });

  /* ------------------------------------------------------------ Money */
  const formatMoney = (cents) => {
    const fmt = theme.moneyFormat || '{{amount_with_space_separator}}€';
    const value = (cents / 100).toFixed(2);
    const [i, d] = value.split('.');
    const withSep = (sep, dec) => i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + dec + d;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: withSep(',', '.'),
      amount_no_decimals: i.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_with_comma_separator: withSep('.', ','),
      amount_no_decimals_with_comma_separator: i.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_with_space_separator: withSep(' ', ','),
      amount_with_apostrophe_separator: withSep("'", '.'),
    }[k] || withSep(' ', ',')))
      // Same rule as Liquid's money_without_trailing_zeros: 100€ but 150,30€.
      .replace(/[.,]00(?=\D*$)/, '');
  };
  window.themeFormatMoney = formatMoney;

  /* ------------------------------------------------------------ Cart drawer */
  const cartDrawer = $('[data-cart-drawer]');
  const cartSections = ['cart-drawer', 'cart-icon-bubble'];

  function openCart() {
    if (!cartDrawer) { location.href = theme.routes.cart; return; }
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    trapFocus(cartDrawer);
    setTimeout(() => $('[data-cart-close]', cartDrawer)?.focus(), 60);
    loadCartRecommendations();
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    releaseFocus(cartDrawer);
  }
  function renderSections(sections, newKey) {
    if (!sections) return;
    if (sections['cart-drawer'] && cartDrawer) {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = $('[data-cart-inner]', doc);
      const current = $('[data-cart-inner]', cartDrawer);
      if (fresh && current) {
        current.replaceWith(fresh);
        if (newKey) $(`[data-key="${CSS.escape(newKey)}"]`, cartDrawer)?.classList.add('is-new');
      }
    }
    if (sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const bubble = $('#cart-icon-bubble');
      const inner = doc.querySelector('.shopify-section') || doc.body;
      if (bubble) {
        bubble.innerHTML = inner.innerHTML;
        $('.cart-count', bubble)?.classList.add('is-bump');
      }
    }
  }
  async function cartFetch(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSections, sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
    return data;
  }

  function flyToCart(fromEl) {
    const img = fromEl?.closest('.card, .product')?.querySelector('img');
    const target = $('#cart-icon-bubble');
    if (!img || !target || reduceMotion.matches) return Promise.resolve();
    const a = img.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const ghost = document.createElement('div');
    ghost.className = 'fly-ghost';
    const size = Math.min(a.width, a.height, 140);
    Object.assign(ghost.style, {
      width: size + 'px', height: size + 'px',
      left: a.left + a.width / 2 - size / 2 + 'px', top: a.top + a.height / 2 - size / 2 + 'px',
      backgroundImage: `url("${img.currentSrc || img.src}")`,
    });
    document.body.appendChild(ghost);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    requestAnimationFrame(() => {
      ghost.style.transform = `translate(${dx}px, ${dy}px) scale(.15)`;
      ghost.style.opacity = '0.2';
    });
    return new Promise((r) => setTimeout(() => { ghost.remove(); r(); }, 720));
  }

  async function addToCart(form, button) {
    const fd = new FormData(form);
    const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    const status = form.querySelector('[data-cart-status]') || form.closest('[data-product]')?.querySelector('[data-cart-status]');
    if (status) status.textContent = '';
    try {
      const data = await cartFetch(theme.routes.cartAdd + '.js', { items });
      const key = data.items?.[0]?.key;
      await flyToCart(button);
      renderSections(data.sections, key);
      button?.classList.add('is-added');
      setTimeout(() => button?.classList.remove('is-added'), 1800);
      if (status) status.textContent = theme.strings.added;
      openCart();
    } catch (err) {
      if (status) status.textContent = err.message;
      else alert(err.message);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-quick-add], [data-product-form]');
    if (!form || !cartDrawer) return;
    e.preventDefault();
    addToCart(form, e.submitter || form.querySelector('[type="submit"]'));
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && cartDrawer) { e.preventDefault(); openCart(); return; }
    if (e.target.closest('[data-cart-close]')) { closeCart(); return; }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line]');
      const input = $('input', line);
      const next = Math.max(0, Number(input.value) + Number(qtyBtn.dataset.qtyChange));
      changeLine(line, next);
    }
    const remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); changeLine(remove.closest('[data-line]'), 0); }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line] input[type="number"]');
    if (input) changeLine(input.closest('[data-line]'), Math.max(0, Number(input.value)));
  });
  // Escape closes the drawer wherever focus is (the drawer content is re-rendered after each change).
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && cartDrawer?.classList.contains('is-open')) closeCart(); });

  async function changeLine(line, quantity) {
    if (!line) return;
    line.style.opacity = '.5';
    try {
      const data = await cartFetch(theme.routes.cartChange + '.js', { id: line.dataset.key, quantity });
      renderSections(data.sections);
      if (document.body.classList.contains('template-cart')) location.reload();
    } catch (err) {
      line.style.opacity = '';
      const msg = line.querySelector('[data-line-error]');
      if (msg) msg.textContent = err.message;
    }
  }

  let recsFor = null;
  async function loadCartRecommendations() {
    const box = cartDrawer && $('[data-cart-recs]', cartDrawer);
    if (!box || !box.dataset.productId || recsFor === box.dataset.productId) return;
    recsFor = box.dataset.productId;
    try {
      const url = `${theme.routes.recommendations}?section_id=cart-recommendations&product_id=${box.dataset.productId}&limit=3&intent=complementary`;
      let html = await (await fetch(url)).text();
      let doc = new DOMParser().parseFromString(html, 'text/html');
      if (!$('[data-recs-item]', doc)) {
        html = await (await fetch(url.replace('complementary', 'related'))).text();
        doc = new DOMParser().parseFromString(html, 'text/html');
      }
      const inner = $('[data-recs]', doc);
      if (inner && $('[data-recs-item]', inner)) { box.innerHTML = ''; box.appendChild(inner); }
    } catch (_) { /* recommendations are a nice-to-have */ }
  }

  /* ------------------------------------------------------------ Search modal + predictive search */
  const modal = $('[data-search-modal]');
  if (modal) {
    const input = $('[data-search-input]', modal);
    const results = $('[data-search-results]', modal);
    const gauge = $('.gauge', modal);
    let ctrl;
    let timer;
    const setOpen = (on) => {
      modal.classList.toggle('is-open', on);
      modal.setAttribute('aria-hidden', String(!on));
      lockScroll(on);
      if (on) { trapFocus(modal); setTimeout(() => input.focus(), 60); }
      else releaseFocus(modal);
    };
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', () => setOpen(true)));
    $$('[data-search-close]', modal).forEach((b) => b.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && modal.classList.contains('is-open')) setOpen(false); });
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = $$('[data-predictive-item]', results);
        if (!items.length) return;
        e.preventDefault();
        const i = items.indexOf(document.activeElement);
        const next = e.key === 'ArrowDown' ? items[(i + 1) % items.length] : items[(i - 1 + items.length) % items.length];
        next.focus();
      }
    });
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      timer = setTimeout(async () => {
        ctrl?.abort();
        ctrl = new AbortController();
        gauge?.style.setProperty('--fill', '.6');
        try {
          const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
          const html = await (await fetch(url, { signal: ctrl.signal })).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = $('[data-predictive]', doc);
          results.innerHTML = inner ? inner.outerHTML : '';
          input.setAttribute('aria-expanded', 'true');
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        } finally {
          gauge?.style.setProperty('--fill', '1');
          setTimeout(() => gauge?.style.setProperty('--fill', '0'), 400);
        }
      }, 180);
    });
  }

  /* ------------------------------------------------------------ Theme editor */
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-hero]', e.target).forEach((h) => h.classList.add('is-ready'));
  });

  window.themeCart = { open: openCart, close: closeCart, add: addToCart };
})();
